import { useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Filter, X } from 'lucide-react';
import { DeadlineCard } from './DeadlineCard';
import type { Deadline, DeadlineCategory, DeadlineStatus } from '@/types/deadlines';
import { CATEGORY_LABELS, STATUS_LABELS } from '@/types/deadlines';

interface DeadlineListProps {
  deadlines: Deadline[];
  onEdit?: (deadline: Deadline) => void;
  onDelete?: (deadline: Deadline) => void;
  onComplete?: (deadline: Deadline) => void;
  onViewChat?: (chatId: string) => void;
  compact?: boolean;
  showFilters?: boolean;
}

export function DeadlineList({
  deadlines,
  onEdit,
  onDelete,
  onComplete,
  onViewChat,
  compact = false,
  showFilters = true,
}: DeadlineListProps) {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<DeadlineCategory | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<DeadlineStatus | 'all'>('all');

  const filteredDeadlines = deadlines.filter(deadline => {
    // Filtro de búsqueda
    if (search) {
      const searchLower = search.toLowerCase();
      const matchesSearch = 
        deadline.title.toLowerCase().includes(searchLower) ||
        deadline.expediente_number?.toLowerCase().includes(searchLower) ||
        deadline.interested_party?.toLowerCase().includes(searchLower) ||
        deadline.description?.toLowerCase().includes(searchLower);
      
      if (!matchesSearch) return false;
    }

    // Filtro de categoría
    if (categoryFilter !== 'all' && deadline.category !== categoryFilter) {
      return false;
    }

    // Filtro de estado
    if (statusFilter !== 'all' && deadline.status !== statusFilter) {
      return false;
    }

    return true;
  });

  const hasActiveFilters = search || categoryFilter !== 'all' || statusFilter !== 'all';

  const clearFilters = () => {
    setSearch('');
    setCategoryFilter('all');
    setStatusFilter('all');
  };

  return (
    <div className="space-y-4">
      {showFilters && (
        <div className="space-y-3">
          {/* Búsqueda */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar plazos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Filtros */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Filter className="h-4 w-4" />
              Filtrar:
            </div>
            
            <Select value={categoryFilter} onValueChange={(v) => setCategoryFilter(v as DeadlineCategory | 'all')}>
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as DeadlineStatus | 'all')}>
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearFilters}
                className="h-8"
              >
                <X className="h-4 w-4 mr-1" />
                Limpiar
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Lista de plazos */}
      <ScrollArea className="h-[400px]">
        {filteredDeadlines.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-muted-foreground">
              {hasActiveFilters 
                ? 'No se encontraron plazos con los filtros seleccionados' 
                : 'No hay plazos registrados'}
            </p>
            {hasActiveFilters && (
              <Button variant="link" onClick={clearFilters}>
                Limpiar filtros
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3 pr-4">
            {filteredDeadlines.map((deadline) => (
              <DeadlineCard
                key={deadline.id}
                deadline={deadline}
                onEdit={onEdit}
                onDelete={onDelete}
                onComplete={onComplete}
                onViewChat={onViewChat}
                compact={compact}
              />
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Contador */}
      <div className="text-xs text-muted-foreground text-center">
        Mostrando {filteredDeadlines.length} de {deadlines.length} plazos
      </div>
    </div>
  );
}
